/**
 * The SPA's single outbound door.
 *
 * Everything the browser fetches goes through the Worker, which holds the OAuth broker and
 * proxies PostgREST and the sidecar; the SPA never talks to a database or to PostgREST
 * directly and never learns a database credential. One client means one place where the
 * bearer token, the ETag cache and the error shape live.
 *
 * ETag/304 support is here rather than in a hook because the read plane's polling story
 * (10 s inspector refresh, refetch-on-focus) depends on 304s being nearly free; a caller
 * that forgets to pass the previous ETag would silently turn every poll into a full body.
 */
import {
  LINK_VERSION,
  MENU_CHECK_VERSION,
  grantPath,
  isMenuCheckReport,
  menuCheckPath,
  type GrantAccepted,
  type GrantRejected,
  type GrantRequest,
  type LinkClaimAccepted,
  type LinkClaimRequest,
  type LinkRejected,
  type MenuCheckReport,
  type WebViewName,
  type WebViewRow,
} from '@dimensionscore/shared';

/**
 * The claim route, spelled once. Not derived from `LINK_PATH` (which is the SPA's own page) — the
 * two happen to share a word and are different things, and folding them together is how a future
 * rename of the page silently re-aims the request.
 */
const LINK_CLAIM_PATH = '/v1/link/claim';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly url: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** A body plus the ETag that produced it, so the next poll can send If-None-Match. */
export interface Cached<T> {
  readonly data: T;
  readonly etag: string | null;
  /** True when the server answered 304 and `data` is the value the caller passed back in. */
  readonly notModified: boolean;
}

export interface ApiClientOptions {
  /**
   * Worker origin. Empty string (the default) means same-origin, which is how Pages serves
   * it in production; a dev/test build points this at `wrangler dev --local`.
   */
  readonly baseUrl?: string;
  /** Returns the current bearer token, or null when logged out. Called per request. */
  readonly getToken?: () => string | null;
  /** Injectable for tests; defaults to global fetch. */
  readonly fetchImpl?: typeof fetch;
}

export class ApiClient {
  private readonly baseUrl: string;
  private readonly getToken: () => string | null;
  private readonly fetchImpl: typeof fetch;

  constructor(options: ApiClientOptions = {}) {
    this.baseUrl = (options.baseUrl ?? '').replace(/\/+$/, '');
    this.getToken = options.getToken ?? (() => null);
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  /** Absolute URL for a path, with query parameters applied in a stable order. */
  url(path: string, query: Readonly<Record<string, string>> = {}): string {
    const suffix = path.startsWith('/') ? path : `/${path}`;
    const entries = Object.entries(query);
    if (entries.length === 0) return `${this.baseUrl}${suffix}`;
    const params = new URLSearchParams(entries.sort(([a], [b]) => a.localeCompare(b)));
    return `${this.baseUrl}${suffix}?${params.toString()}`;
  }

  /**
   * Conditional GET. Pass `previous` from the last call to get a 304 short-circuit; the
   * returned `Cached` then carries the same `data` with `notModified: true`.
   */
  async get<T>(path: string, query?: Readonly<Record<string, string>>, previous?: Cached<T>): Promise<Cached<T>> {
    const url = this.url(path, query);
    const headers = new Headers({ accept: 'application/json' });
    const token = this.getToken();
    if (token !== null) headers.set('authorization', `Bearer ${token}`);
    if (previous?.etag) headers.set('if-none-match', previous.etag);

    const response = await this.fetchImpl(url, { headers, credentials: 'omit' });

    if (response.status === 304 && previous) {
      return { data: previous.data, etag: previous.etag, notModified: true };
    }
    if (!response.ok) {
      throw new ApiError(response.status, url, await describeFailure(response));
    }
    return { data: (await response.json()) as T, etag: response.headers.get('etag'), notModified: false };
  }

  /**
   * Read a `web.*` view. The row type follows from the view name, so a caller cannot ask
   * for `player_homes` and destructure a reward field.
   */
  async readView<N extends WebViewName>(
    view: N,
    query?: Readonly<Record<string, string>>,
    previous?: Cached<WebViewRow<N>[]>,
  ): Promise<Cached<WebViewRow<N>[]>> {
    return this.get<WebViewRow<N>[]>(`/v1/db/${view}`, query, previous);
  }

  /**
   * File a grant. THE ONLY WRITE THIS CLIENT CAN MAKE, and there is deliberately no generic
   * `post` beside it for someone to reach for: the read plane is GET-only all the way down
   * (the Worker answers 405 on `/v1/db/*` for anything else), and this is the single verb on
   * the single path the web tier is allowed.
   *
   * IT DOES NOT THROW ON A REFUSAL. A 403, a 422 or a 503 here is an answer the UI has to
   * render - the tier is too low, the payload carries a command, the sidecar holds no write
   * credential - not an exception. Only a request that never produced an HTTP answer at all
   * rejects. `ApiError` is for the read path, where every non-200 genuinely is a failure.
   *
   * A 201 MEANS "FILED", NOT "GRANTED", and the response carries no row id to follow: the
   * write role holds no SELECT on `web_outbox`. The caller learns the outcome by reading
   * `web.outbox_status` back with a READ token, which is what `useGrantWatch` does.
   */
  async grant(playerUuid: string, request: GrantRequest): Promise<GrantOutcome> {
    const url = this.url(grantPath(playerUuid));
    const headers = new Headers({ accept: 'application/json', 'content-type': 'application/json' });
    const token = this.getToken();
    if (token !== null) headers.set('authorization', `Bearer ${token}`);

    const response = await this.fetchImpl(url, { method: 'POST', headers, body: JSON.stringify(request), credentials: 'omit' });
    const body = (await response.json().catch(() => null)) as GrantAccepted | GrantRejected | null;

    if (response.status === 201 && body !== null && 'ok' in body) return { filed: true, accepted: body };
    /*
     * The edge can refuse before the sidecar is ever reached - 429 from the Worker's rate
     * limiter, 502 when the sidecar is down, a bare 401 with no body at all - and those
     * refusals are not in `GrantErrorCode`, which is the SIDECAR's vocabulary. Inventing a
     * member of it here would be a lie the UI then explains confidently; `edge_refused`
     * says exactly what is known, and the status travels beside it either way.
     */
    if (body !== null && 'error' in body) return { filed: false, status: response.status, rejected: body };
    return {
      filed: false,
      status: response.status,
      rejected: { error: 'edge_refused', detail: response.statusText === '' ? `the edge answered ${response.status}` : response.statusText },
    };
  }

  /**
   * Present the code `/link` printed in game, and file the binding intent.
   *
   * THE THIRD NAMED WRITE, and the only one a non-staff person will ever make. It sends the code
   * and NOTHING ELSE: the Discord account comes from the `sub` of the token the sidecar verifies,
   * and the player comes from the sidecar's own record of the game server's push. A `discordId` or
   * `playerUuid` field here would be a value the caller got to choose about an identity, which is
   * precisely what the handshake exists to prevent — and the sidecar refuses an unknown field
   * rather than ignoring it, so adding one would fail loudly rather than silently do nothing.
   *
   * 202 means FILED. The page must not say "linked".
   */
  async claimLink(code: string): Promise<LinkOutcome> {
    const url = this.url(LINK_CLAIM_PATH);
    const headers = new Headers({ accept: 'application/json', 'content-type': 'application/json' });
    const token = this.getToken();
    if (token !== null) headers.set('authorization', `Bearer ${token}`);

    const response = await this.fetchImpl(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ version: LINK_VERSION, code } satisfies LinkClaimRequest),
      credentials: 'omit',
    });
    const body = (await response.json().catch(() => null)) as LinkClaimAccepted | LinkRejected | null;

    if (response.status === 202 && body !== null && 'ok' in body) return { filed: true, accepted: body };
    // The edge can refuse before the sidecar is reached — 429 from the rate limiter, 502 when the
    // sidecar is down, a bare 401 with no body. Those are not LinkErrorCodes, which is the
    // SIDECAR's vocabulary, so they are reported as what they are.
    if (body !== null && 'error' in body) return { filed: false, status: response.status, rejected: body };
    return {
      filed: false,
      status: response.status,
      rejected: { error: 'edge_refused', detail: response.statusText === '' ? `the edge answered ${response.status}` : response.statusText },
    };
  }

  /**
   * Ask the authoritative validator what is wrong with a menu document.
   *
   * THE SECOND NAMED WRITE, and named for the same reason the first is: there is no generic
   * `post` on this client, on purpose, so that every request that leaves the browser with a
   * body is a method someone had to add deliberately. This one is a QUERY with a body - it
   * stores nothing and changes nothing - which is why it has no idempotency story and why a
   * refusal is an answer rather than an exception.
   *
   * IT NEVER THROWS FOR A VERDICT IT DID NOT GET. A deployment with nothing behind the route
   * answers 501 and the editor says "this deployment cannot check menus", which is true and
   * is not the same statement as "this menu is fine". Only a request that produced no HTTP
   * answer at all rejects, and even that is caught by the caller. See
   * `@dimensionscore/shared`'s menucheck module for who answers and why it varies.
   */
  async checkMenu(code: string, menuId: string, json: string): Promise<MenuCheckOutcome> {
    const url = this.url(menuCheckPath(code));
    const headers = new Headers({ accept: 'application/json', 'content-type': 'application/json' });
    const token = this.getToken();
    if (token !== null) headers.set('authorization', `Bearer ${token}`);

    let response: Response;
    try {
      response = await this.fetchImpl(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ version: MENU_CHECK_VERSION, menuId, json }),
        credentials: 'omit',
      });
    } catch {
      return { checked: false, status: 0, detail: 'the editor backend could not be reached.' };
    }

    const body: unknown = await response.json().catch(() => null);
    if (response.ok && isMenuCheckReport(body)) return { checked: true, report: body };
    const detail =
      typeof body === 'object' && body !== null && typeof (body as { detail?: unknown }).detail === 'string'
        ? (body as { detail: string }).detail
        : `the backend answered ${response.status}.`;
    return { checked: false, status: response.status, detail };
  }
}

/** What a menu check produced: a verdict, or an account of why there is none. */
export type MenuCheckOutcome =
  | { readonly checked: true; readonly report: MenuCheckReport }
  | { readonly checked: false; readonly status: number; readonly detail: string };

/**
 * A refusal that never reached the sidecar, so it carries none of its codes.
 *
 * Kept apart from {@link GrantErrorCode} rather than folded into it: that type is the
 * sidecar's contract and every member of it is a decision the sidecar took. This one means
 * "something between the browser and the sidecar said no", which the UI words differently.
 */
export interface EdgeRefusal {
  readonly error: 'edge_refused';
  readonly detail?: string;
}

/** What a grant attempt produced. Never an exception for an answer a server actually gave. */
export type GrantOutcome =
  | { readonly filed: true; readonly accepted: GrantAccepted }
  | { readonly filed: false; readonly status: number; readonly rejected: GrantRejected | EdgeRefusal };

/**
 * What a link claim produced. Same shape and same rule as {@link GrantOutcome}: a refusal the
 * server actually gave is a value, and only a request that produced no HTTP answer at all rejects.
 *
 * `filed: true` means an INTENT EXISTS, not that the account is linked. The mod's drain decides
 * that on its next tick — see the sidecar's `link.ts`.
 */
export type LinkOutcome =
  | { readonly filed: true; readonly accepted: LinkClaimAccepted }
  | { readonly filed: false; readonly status: number; readonly rejected: LinkRejected | EdgeRefusal };

async function describeFailure(response: Response): Promise<string> {
  const text = await response.text().catch(() => '');
  const trimmed = text.trim();
  return trimmed.length > 0 ? `${response.status} ${response.statusText}: ${trimmed.slice(0, 500)}` : `${response.status} ${response.statusText}`;
}

/**
 * Build-time configured client for the running app. `VITE_API_BASE` is empty in the Pages
 * build (same-origin Worker) and set to the wrangler dev origin locally.
 */
export function createApiClient(getToken?: () => string | null): ApiClient {
  const options: ApiClientOptions = { baseUrl: import.meta.env['VITE_API_BASE'] ?? '' };
  return new ApiClient(getToken ? { ...options, getToken } : options);
}
